/**
 * Full dialog modal: overlay, focus trap, ESC/overlay click, portal.
 *
 * - `variant`: changes default icon/header (confirm, delete, loading…).
 * - Footer actions: `primaryAction` / `secondaryAction`, or fully custom `footer`.
 * - Stacked modals: `stackIndex={1}` for inner layer (darker overlay + higher z-index).
 * - Wide form / layout: `size` + `containerClassName`; fullscreen uses `size="fullscreen"`.
 * Peer: `lucide-react` (default icons per variant).
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { cn } from './cn';
import Button from './Button';
const sizeMaxClass = {
    sm: 'max-w-[360px]',
    md: 'max-w-[480px]',
    lg: 'max-w-[720px]',
};
function getFocusableElements(root) {
    const sel = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(root.querySelectorAll(sel)).filter((el) => el.offsetParent !== null || el.getClientRects().length > 0);
}
function DefaultVariantIcon({ variant }) {
    if (variant === 'confirm') {
        return (React.createElement("span", { className: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200/80", "aria-hidden": true },
            React.createElement(AlertTriangle, { className: "h-6 w-6", strokeWidth: 2 })));
    }
    if (variant === 'delete') {
        return (React.createElement("span", { className: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 ring-1 ring-red-200/80", "aria-hidden": true },
            React.createElement(AlertTriangle, { className: "h-6 w-6", strokeWidth: 2 })));
    }
    if (variant === 'info') {
        return (React.createElement("span", { className: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/80", "aria-hidden": true },
            React.createElement(CheckCircle2, { className: "h-6 w-6", strokeWidth: 2 })));
    }
    if (variant === 'loading') {
        return (React.createElement("span", { className: "flex h-11 w-11 shrink-0 items-center justify-center text-primary", "aria-hidden": true },
            React.createElement(Loader2, { className: "h-8 w-8 animate-spin", strokeWidth: 2 })));
    }
    return null;
}
const Modal = ({ isOpen, onClose, title, subtitle, variant = 'default', icon, size = 'md', children, footer, primaryAction, secondaryAction, closeOnOverlayClick = true, closeOnEsc = true, showCloseButton = true, preventClose = false, scrollable = false, progress = null, className, containerClassName, stackIndex = 0, customHeader, rootClassName, verticalAlign = 'center', bodyClassName, footerClassName, }) => {
    const reactId = useId();
    const titleId = `${reactId}-modal-title`;
    const subtitleId = `${reactId}-modal-subtitle`;
    const bodyDescId = `${reactId}-modal-body`;
    const panelRef = useRef(null);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [entered, setEntered] = useState(false);
    const previouslyFocused = useRef(null);
    const locked = preventClose || variant === 'loading';
    const effectiveShowClose = showCloseButton && !locked;
    const effectiveScrollable = scrollable || variant === 'form';
    const effectivePrimary = primaryAction !== null && primaryAction !== void 0 ? primaryAction : (variant === 'info'
        ? { label: 'Got it', onClick: onClose }
        : undefined);
    const tryClose = useCallback(() => {
        if (preventClose || variant === 'loading')
            return;
        onClose();
    }, [onClose, preventClose, variant]);
    useEffect(() => {
        if (isOpen) {
            previouslyFocused.current = document.activeElement;
            setShouldRender(true);
            const id = requestAnimationFrame(() => setEntered(true));
            return () => cancelAnimationFrame(id);
        }
        setEntered(false);
        const t = window.setTimeout(() => {
            var _a, _b;
            setShouldRender(false);
            (_b = (_a = previouslyFocused.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a, { preventScroll: true });
            previouslyFocused.current = null;
        }, 150);
        return () => window.clearTimeout(t);
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen || !closeOnEsc || locked)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                tryClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, closeOnEsc, locked, tryClose]);
    useEffect(() => {
        var _a;
        if (!isOpen || !entered || !panelRef.current)
            return;
        const root = panelRef.current;
        const handleKeyDown = (e) => {
            if (e.key !== 'Tab' || !root)
                return;
            const list = getFocusableElements(root);
            if (list.length === 0)
                return;
            const first = list[0];
            const last = list[list.length - 1];
            const active = document.activeElement;
            if (e.shiftKey) {
                if (active === first || !root.contains(active)) {
                    e.preventDefault();
                    last.focus();
                }
            }
            else if (active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        const list = getFocusableElements(root);
        const toFocus = (_a = list[0]) !== null && _a !== void 0 ? _a : root;
        window.setTimeout(() => toFocus.focus({ preventScroll: true }), 0);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, entered]);
    useEffect(() => {
        if (!isOpen)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);
    if (!shouldRender)
        return null;
    const showVariantIcon = (variant === 'confirm' || variant === 'delete' || variant === 'info' || variant === 'loading') &&
        !customHeader;
    const defaultIcon = icon !== null && icon !== void 0 ? icon : (showVariantIcon ? React.createElement(DefaultVariantIcon, { variant: variant }) : null);
    const bodyRowLayout = !customHeader && (variant === 'confirm' || variant === 'delete' || variant === 'info') && defaultIcon;
    const hasBodyText = React.Children.toArray(children).length > 0;
    const describedBy = [subtitle ? subtitleId : null, hasBodyText ? bodyDescId : null].filter(Boolean).join(' ') || undefined;
    const zWrapper = stackIndex === 1 ? 'z-[60]' : 'z-50';
    const overlayTint = stackIndex === 1 ? 'bg-black/60' : 'bg-black/50';
    const widthClass = size === 'fullscreen'
        ? 'h-[100dvh] w-[100vw] max-w-none rounded-none'
        : cn('w-full', sizeMaxClass[size], containerClassName);
    const builtInActions = variant !== 'loading' && (secondaryAction != null || effectivePrimary != null);
    const footerContent = footer !== null && footer !== void 0 ? footer : (builtInActions ? (React.createElement("div", { className: "flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3" },
        secondaryAction ? (React.createElement(Button, { type: "button", variant: "outline", onClick: secondaryAction.onClick }, secondaryAction.label)) : null,
        effectivePrimary ? (React.createElement(Button, { type: "button", variant: variant === 'delete' ? 'destructive' : 'primary', onClick: effectivePrimary.onClick, disabled: effectivePrimary.disabled, loading: !!effectivePrimary.loading, "aria-busy": effectivePrimary.loading ? true : undefined }, effectivePrimary.label)) : null)) : null);
    const hasFooter = footerContent != null;
    const modalInner = (React.createElement("div", { className: cn('fixed inset-0 flex max-sm:items-end sm:justify-center', size === 'fullscreen' ? 'p-0 sm:p-0' : 'sm:p-4', size === 'fullscreen'
            ? 'sm:items-stretch sm:py-0'
            : verticalAlign === 'start'
                ? 'sm:items-start sm:py-10'
                : 'sm:items-center', zWrapper, 'pointer-events-none', rootClassName) },
        React.createElement("div", { className: cn('pointer-events-auto absolute inset-0 transition-opacity duration-150 ease-out', overlayTint, entered ? 'opacity-100' : 'opacity-0'), "aria-hidden": "true", onClick: () => {
                if (closeOnOverlayClick && !locked)
                    tryClose();
            }, onKeyDown: (e) => e.stopPropagation(), role: "presentation" }),
        React.createElement("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": customHeader ? undefined : titleId, "aria-label": customHeader ? 'Dialog' : undefined, "aria-describedby": describedBy, className: cn('pointer-events-auto relative z-10 flex max-h-[100dvh] flex-col overflow-hidden border border-border bg-card text-card-foreground shadow-xl transition-[opacity,transform] duration-150 ease-out max-sm:max-h-[min(92dvh,100dvh)] max-sm:rounded-b-none max-sm:rounded-t-2xl sm:max-h-[min(90dvh,100vh)] sm:rounded-2xl', widthClass, entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1 scale-[0.97] opacity-0 max-sm:translate-y-4', size === 'fullscreen' && 'sm:m-0', className), onClick: (e) => e.stopPropagation() },
            customHeader ? (React.createElement("div", { className: "shrink-0 border-b border-border" }, customHeader)) : (React.createElement("header", { className: "shrink-0 border-b border-border px-5 pb-4 pt-5" },
                React.createElement("div", { className: "flex items-start justify-between gap-3" },
                    React.createElement("div", { className: "min-w-0 flex-1 space-y-1" },
                        React.createElement("h2", { id: titleId, className: "text-lg font-semibold leading-tight tracking-tight text-foreground" }, title !== null && title !== void 0 ? title : '\u00a0'),
                        subtitle != null && subtitle !== '' && (React.createElement("p", { id: subtitleId, className: "text-sm text-muted-foreground" }, subtitle))),
                    effectiveShowClose ? (React.createElement("button", { type: "button", onClick: tryClose, className: cn('-m-1 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors', 'hover:bg-muted hover:text-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'), "aria-label": "Close modal" },
                        React.createElement(X, { className: "h-5 w-5", strokeWidth: 2 }))) : null))),
            React.createElement("div", { id: bodyDescId, className: cn('min-h-0 flex-1 px-5 py-4 text-sm text-foreground', effectiveScrollable && 'overflow-y-auto overscroll-contain', bodyRowLayout && 'flex flex-col gap-4 sm:flex-row sm:items-start', bodyClassName) }, bodyRowLayout ? (React.createElement(React.Fragment, null,
                defaultIcon,
                React.createElement("div", { className: "min-w-0 flex-1 space-y-2" }, children))) : variant === 'loading' ? (React.createElement("div", { className: "flex flex-col items-center gap-4 py-2 text-center" }, defaultIcon !== null && defaultIcon !== void 0 ? defaultIcon : React.createElement(Loader2, { className: "h-10 w-10 animate-spin text-primary", "aria-hidden": true }),
                children,
                progress != null && (React.createElement("div", { className: "h-2 w-full max-w-xs overflow-hidden rounded-full bg-muted", role: "progressbar", "aria-valuenow": progress, "aria-valuemin": 0, "aria-valuemax": 100 },
                    React.createElement("div", { className: "h-full rounded-full bg-primary transition-[width] duration-300", style: { width: `${Math.min(100, Math.max(0, progress))}%` } }))))) : (children)),
            hasFooter ? (React.createElement("footer", { className: cn('shrink-0 border-t border-border px-5 py-4', footerClassName) }, footerContent)) : null)));
    return createPortal(modalInner, document.body);
};
export default Modal;
